import {
  Box, Typography, Grid, alpha, useTheme, Stack, Divider,
  Tooltip, IconButton, Skeleton, Paper
} from "@mui/material";
import { useEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { TbShieldCheck, TbActivity, TbRefresh } from "react-icons/tb";
import { LuPackage, LuLayers, LuCalendar } from "react-icons/lu";
import { FaRecycle, FaIndustry } from "react-icons/fa";
import { MdHub } from "react-icons/md";

import { Usage } from "../../db_access/usage";
import DonutChart from "../../components/chart/DonutChart";
import MetricCard from "../../app_components/MetricCard";
import AgGrid from "../../components/grid/AgGrid";
import SegmentedTabs from "../../components/tabs/SegmentedTabs";
import DateSelector from "../../components/input/DateSelector";
import CheckBox from "../../components/input/CheckBox";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import RefreshButton from "../../components/button/RefreshButton";
import SearchBox from "../../components/input/SearchBox";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";
import { leftAlignColDef, rightAlignColDef, defaultColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import UsageGlobalView from "./UsageGlobalView";

// ── Icon map for module sections (matches sidebar icons) ─────────────────────
const SECTION_ICON_MAP = {
  'Product': LuPackage,
  'Structure': FaIndustry,
  'Project': LuLayers,
  'Supply Chain': MdHub,
  'Reused Product': FaRecycle,
  'Partnership': MdHub,
};
const getModuleIcon = (sectionName) => SECTION_ICON_MAP[sectionName] || TbActivity;

// ── Styling helper (Engagement pattern) ──────────────────────────────────────
const cellTextSx = (isDark) => ({
  fontFamily: GRAPH_PALETTE.font.family,
  fontSize: GRAPH_PALETTE.font.sizes.standard,
  fontWeight: GRAPH_PALETTE.font.weights.standard,
  color: isDark ? "#f1f5f9" : "#1e293b",
});

const UsageMine = ({ startDate, endDate, setStartDate, setEndDate, refresh, setGlobalStats }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tok = getOrgTokens(isDark);
  const mc = ORG_PALETTE.metricCards;
  const sh = tok.sectionHeading;

  const [usages, setUsages] = useState([]);
  const [timedUsages, setTimedUsages] = useState([]);
  const [sectionUsages, setSectionUsages] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [localRefresh, setLocalRefresh] = useState(0);
  const [globalView, setGlobalView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const gridRef = useRef(null);

  useEffect(() => {
    setIsLoading(true);
    Usage.get_mine()
      .then((res) => res.json())
      .then((data) => {
        setUsages(Array.isArray(data) ? data : []);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching usage:", err);
        setIsLoading(false);
      });
  }, [refresh]);

  useEffect(() => {
    const sectionTotals = timedUsages.reduce((acc, usage) => {
      const section = usage.section_name;
      const point = Number(usage.point) || 0;
      acc[section] = (acc[section] || 0) + point;
      return acc;
    }, {});
    const sectionUsagesArray = Object.entries(sectionTotals).map(([section, total], i) => ({
      id: i,
      section_name: section,
      point: total,
    }));
    setSectionUsages(sectionUsagesArray);
  }, [timedUsages]);

  useEffect(() => {
    const filtered = usages.filter(u => {
      const ts = dayjs(u.timestamp);
      return (ts.isAfter(startDate) || ts.isSame(startDate, 'day')) &&
        (ts.isBefore(endDate) || ts.isSame(endDate, 'day'));
    });
    setTimedUsages(filtered);
  }, [usages, startDate, endDate]);

  const totalPoints = useMemo(() => timedUsages.reduce((sum, curr) => sum + (Number(curr.point) || 0), 0), [timedUsages]);

  useEffect(() => {
    if (setGlobalStats) {
      setGlobalStats({
        capacity: totalPoints.toLocaleString(),
        modules: sectionUsages.length,
        sync: timedUsages.length > 0 ? dayjs(timedUsages[0].timestamp).format('MMM DD, HH:mm') : '—',
        totalEntries: timedUsages.length
      });
    }
  }, [totalPoints, sectionUsages, timedUsages, setGlobalStats]);

  const donutData = useMemo(() => {
    const mc = ORG_PALETTE.metricCards;
    const colors = [mc.scope2.accent, mc.chain.accent, mc.scope1.accent, mc.scope3.accent, mc.cost.accent, mc.gwp.accent];
    const filtered = [...sectionUsages].sort((a, b) => b.point - a.point).slice(0, 6);
    return filtered.map((s, i) => ({
      label: s.section_name,
      value: s.point,
      color: colors[i % colors.length],
      percent: totalPoints > 0 ? (s.point / totalPoints) * 100 : 0
    }));
  }, [sectionUsages, totalPoints]);

  const topSections = useMemo(() => {
    return [...sectionUsages].sort((a, b) => b.point - a.point).slice(0, 4);
  }, [sectionUsages]);

  const filteredUsages = useMemo(() => {
    return timedUsages.filter(u =>
      !searchText ||
      u.section_name.toLowerCase().includes(searchText.toLowerCase()) ||
      u.endpoint_name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [timedUsages, searchText]);

  // Ag-Grid Column Definitions
  const colDefs = useMemo(() => [
    {
      headerName: t("Module Section"),
      field: "section_name",
      cellStyle: { ...tok.gridCellText, ...leftAlignColDef.cellStyle },
      ...defaultColDef,
      cellRenderer: (params) => {
        const mc = ORG_PALETTE.metricCards;
        const colors = { 'Product': mc.scope2.accent, 'Project': mc.chain.accent, 'Building': mc.scope1.accent };
        const color = colors[params.value] || mc.scope3.accent;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, boxShadow: `0 0 8px ${alpha(color, 0.4)}` }} />
            <Typography variant="body2" sx={{ ...tok.gridCellText }}>{t(params.value)}</Typography>
          </Box>
        );
      }
    },
    {
      headerName: t("Endpoint Trace"),
      field: "endpoint_name",
      ...leftAlignColDef,
      ...defaultColDef,
      cellStyle: { ...tok.gridCellText, color: tok.text.muted, ...leftAlignColDef.cellStyle }
    },
    {
      headerName: t("Activity Time"),
      field: "timestamp",
      valueFormatter: (p) => dayjs(p.value).format('DD MMM YYYY HH:mm'),
      ...rightAlignColDef,
      ...defaultColDef,
      cellStyle: { ...tok.gridCellText, opacity: 0.7, ...rightAlignColDef.cellStyle }
    },
    {
      headerName: t("Points"),
      field: "point",
      ...rightAlignColDef, ...defaultColDef,
      cellStyle: {
        ...tok.gridCellText,
        ...rightAlignColDef.cellStyle
      },
      valueFormatter: (p) => `+${Number(p.value).toLocaleString()}`
    }
  ], [tok, t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: tok.spacing.pageGap, animation: 'fadeIn 0.6s ease-out' }}>

      {/* 1. HEADER SUMMARY & FILTERS */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        pb: tok.sectionHeading.titlePb,
        borderBottom: tok.sectionHeading.borderBottom
      }}>
        <Typography sx={{
          ...tok.sectionHeading, borderLeft: `${tok.sectionHeading.barWidth} solid ${tok.sectionHeading.barColor}`, pl: 1, borderBottom: 'none'
        }}>
          {t("My Usage Dashboard")}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: '160px' }}><DateSelector label="" value={startDate} onChange={setStartDate} /></Box>
            <Box sx={{ width: 8, height: '2px', bgcolor: tok.border.card, borderRadius: 1 }} />
            <Box sx={{ width: '160px' }}><DateSelector label="" value={endDate} onChange={setEndDate} /></Box>
          </Box>
        </Box>
      </Box>

      {/* 2. METRICS */}
      <Box sx={{ display: 'flex', gap: tok.spacing.metricCardGap, flexWrap: 'wrap', width: '100%' }}>
        {isLoading ? (
          Array.from(new Array(4)).map((_, i) => (
            <Box key={i} sx={{ flex: '1 1 0', minWidth: '240px' }}>
              <Paper sx={{ p: ORG_PALETTE.spacing.chartPad, width: 1, height: '140px', borderRadius: tok.radius.card, border: '1px solid', borderColor: tok.border.card, bgcolor: tok.bg.card }}>
                <Skeleton variant="circular" width={44} height={44} sx={{ mb: 2 }} />
                <Skeleton width="60%" height={32} />
                <Skeleton width="40%" />
              </Paper>
            </Box>
          ))
        ) : (
          topSections.map((s, i) => {
            const typeKey = ['chain', 'scope2', 'scope1', 'scope3'][i % 4];
            return (
              <Box key={i} sx={{ flex: '1 1 0', minWidth: '240px' }}>
                <MetricCard
                  title={t(s.section_name)}
                  value={s.point}
                  unit="pts"
                  icon={getModuleIcon(s.section_name)}
                  type={typeKey}
                  bg={mc[typeKey].bg}
                  delay={i * 0.1}
                />
              </Box>
            )
          })
        )}
      </Box>

      {/* 4. ANALYTICS & AUDIT LOG */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: tok.spacing.pageGap, flexWrap: { xs: 'wrap', lg: 'nowrap' },
        bgcolor: tok.bg.card, p: tok.spacing.pageGap, borderRadius: tok.radius.card, border: '1px solid', borderColor: tok.border.card,
        boxShadow: tok.shadow.card
      }}>
        {isLoading ? (
          <>
            <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'center', width: { xs: '100%', lg: 280 } }}>
              <Skeleton variant="circular" width={280} height={280} />
            </Box>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {Array.from(new Array(4)).map((_, i) => (
                <Box key={i}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Skeleton width="30%" />
                    <Skeleton width="20%" />
                  </Box>
                  <Skeleton variant="rounded" width="100%" height={8} />
                </Box>
              ))}
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'center', width: { xs: '100%', lg: 'auto' } }}>
              {donutData.length > 0 ? (
                <DonutChart
                  data={donutData}
                  size={280}
                  strokeWidth={35}
                  centerContent={
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ ...cellTextSx(isDark), display: 'block', color: isDark ? '#94a3b8' : '#64748b', fontSize: GRAPH_PALETTE.font.sizes.donutTotalSmall }}>
                        {t("Total")}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: GRAPH_PALETTE.font.weights.heavy, color: tok.text.primary, lineHeight: 1.2, fontSize: '28px' }}>
                        {totalPoints.toLocaleString()}
                      </Typography>
                      <Typography sx={{ ...cellTextSx(isDark), color: isDark ? '#94a3b8' : '#64748b', fontSize: GRAPH_PALETTE.font.sizes.donutTotalSmall }}>
                        {t("Points")}
                      </Typography>
                    </Box>
                  }
                />
              ) : (
                <Box sx={{ width: 280, height: 280, borderRadius: '50%', border: '4px dashed', borderColor: tok.border.card, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ ...cellTextSx(isDark), fontWeight: GRAPH_PALETTE.font.weights.medium }}>{t("Inconclusive Data")}</Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ flexGrow: 1, pr: 1, width: '100%' }}>
              <Box sx={{ mt: 1 }}>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ ...tok.sectionHeading, borderLeft: `${tok.sectionHeading.barWidth} solid ${tok.sectionHeading.barColor}`, pl: 1, fontSize: '16px', borderBottom: 'none' }}>
                    {t("Module Consumption Distribution")}
                  </Typography>
                </Box>
                {donutData.map((d, i) => (
                  <Box key={i} sx={{ mb: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: d.color, flexShrink: 0 }} />
                        <Typography sx={{
                          fontFamily: GRAPH_PALETTE.font.family,
                          fontSize: GRAPH_PALETTE.font.sizes.axisLabel,
                          fontWeight: GRAPH_PALETTE.font.weights.standard,
                          color: tok.text.primary
                        }}>
                          {t(d.label)}
                        </Typography>
                      </Box>
                      <Typography sx={{
                        fontFamily: GRAPH_PALETTE.font.family,
                        fontSize: GRAPH_PALETTE.font.sizes.axisLabel,
                        fontWeight: GRAPH_PALETTE.font.weights.standard,
                        color: tok.text.primary
                      }}>
                        {d.value.toLocaleString()} ({Math.round(d.percent)}%)
                      </Typography>
                    </Box>
                    <Box sx={{ height: 4, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9", overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${d.percent}%`, bgcolor: d.color, borderRadius: 2, transition: "width 0.6s ease" }} />
                    </Box>
                  </Box>
                ))}
                <Divider sx={{ mt: 3, mb: 2, opacity: 0.35 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.standard, fontWeight: GRAPH_PALETTE.font.weights.medium, color: tok.text.muted }}>
                    {t("Engagement Index:")}
                  </Typography>
                  <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.standard, fontWeight: GRAPH_PALETTE.font.weights.bold, color: tok.text.primary }}>
                    {sectionUsages.length} {t("Active Modules")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* 5. AUDIT LOG */}
      <Box sx={{ border: '1px solid', borderColor: tok.border.card, borderRadius: tok.radius.card, bgcolor: tok.bg.card, boxShadow: tok.shadow.card, overflow: 'hidden' }}>
        <TableHeaderContainer>
          <TableTitleContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TbShieldCheck size={20} color={tok.bg.tabActive} />
              <Typography variant="Table Title">{t("Workspace Activity Journal")}</Typography>
            </Box>
          </TableTitleContainer>
          <TableHeaderToolsContainer>
            <SearchBox text={searchText} setText={setSearchText} placeholder={t("Trace activities...")} width={300} />
            <ExportAsXLSXButton data={timedUsages} filename="my_usage_audit.xlsx" />
            <RefreshButton setRefresh={() => setLocalRefresh(p => p + 1)} />
          </TableHeaderToolsContainer>
        </TableHeaderContainer>
        <Box sx={{ p: 0 }}>
          <AgGrid
            columnDefs={colDefs}
            rowData={filteredUsages}
            quickFilterText={searchText}
            domLayout="autoHeight"
            pagination={true}
            footerLeftContent={
              <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
                <CheckBox checked={globalView} onChange={(e) => setGlobalView(e.target.checked)} label={t("Global Insights")} />
              </Box>
            }
          />
        </Box>
      </Box>

      {/* GLOBAL VIEW OVERLAY */}
      {globalView && (
        <Box>
          <UsageGlobalView
            usages={timedUsages}
            sectionUsages={sectionUsages}
            totalPoints={totalPoints}
            entityLabel={t("My Workspace")}
            accentColor={tok.bg.tabActive}
          />
        </Box>
      )}

      <style jsx="true">{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </Box>
  );
};

export default UsageMine;
